import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Mail, Check, Loader2, Trash2, Send } from 'lucide-react';

const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'America/Halifax', label: 'Halifax (AST/ADT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Winnipeg', label: 'Winnipeg (CST/CDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'UTC', label: 'UTC' },
];

const defaults = {
  enabled: false,
  recipient_email: '', recipient_name: '',
  recipient_email_2: '', recipient_name_2: '',
  recipient_email_3: '', recipient_name_3: '',
  send_time: '00:00',
  send_timezone: 'Australia/Sydney',
};

const EmailNotifications = () => {
  const { organizationId, isStaff } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [settings, setSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      const { data } = await supabase
        .from('email_notification_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('notification_type', 'daily')
        .maybeSingle();
      if (data) setSettings({ ...defaults, ...data });
      setLoading(false);
    })();
  }, [organizationId]);

  const update = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const recipientCount = [settings.recipient_email, settings.recipient_email_2, settings.recipient_email_3].filter(e => e && e.trim()).length;

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        organization_id: organizationId,
        notification_type: 'daily',
        enabled: settings.enabled,
        recipient_email: settings.recipient_email || '',
        // subject_line and recipient_name are NOT NULL with no default, so an
        // INSERT that omits them fails — which made this page unsaveable for
        // any org without a pre-existing row. The name field is optional in the
        // UI, so fall back to an empty string rather than null.
        recipient_name: settings.recipient_name || '',
        subject_line: settings.subject_line || 'TutorTracer Daily Report',
        recipient_email_2: settings.recipient_email_2 || null,
        recipient_name_2: settings.recipient_name_2 || null,
        recipient_email_3: settings.recipient_email_3 || null,
        recipient_name_3: settings.recipient_name_3 || null,
        send_time: settings.send_time,
        send_timezone: settings.send_timezone,
      };
      const { error } = await supabase
        .from('email_notification_settings')
        .upsert(payload, { onConflict: 'organization_id,notification_type' });
      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('save error', e);
      alert(`Failed to save: ${e.message || e}`);
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestStatus(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-daily-issues-email', {
        body: { organization_id: organizationId, isTest: true },
      });
      if (error) throw error;
      setTestStatus({ ok: true, msg: `Test sent to ${data?.recipients || recipientCount} recipient(s). ${data?.issues_total || 0} issues in last 24h.` });
    } catch (e) {
      setTestStatus({ ok: false, msg: e.message || 'Failed to send. Make sure the edge function is deployed.' });
    }
    setTesting(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b] focus:ring-[#3f3f46]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999] focus:ring-[#ccc]'}`;
  const toggleClass = (on) => `relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${on ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`;
  const dotClass = (on) => `inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`;

  // Automatic email reports are set up by TutorTracer staff for now; customers
  // see how to request them instead of the configuration screen.
  if (!isStaff) {
    return (
      <div className="w-full">
        <div className={`rounded-lg border p-8 text-center ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
          <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <Mail className={`w-6 h-6 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} strokeWidth={1.5} />
          </div>
          <h1 className={`text-lg font-semibold font-[Manrope] mb-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Automatic email reports</h1>
          <p className={`text-sm max-w-md mx-auto ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            Please contact support to set up automatic email notifications, such as daily issue summaries and weekly reports.
          </p>
          <a href="mailto:support@tutortracer.com?subject=Set%20up%20email%20notifications"
             className="inline-block mt-5 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90">
            Contact support
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    const sk = isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]';
    return (
      <div className="space-y-6 animate-pulse">
        <div className={`h-7 w-48 rounded ${sk}`} />
        <div className={`${cardClass} p-6 space-y-4`}>
          <div className={`h-5 w-32 rounded ${sk}`} />
          <div className={`h-10 w-full rounded ${sk} opacity-30`} />
          <div className={`h-10 w-full rounded ${sk} opacity-30`} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Email Notifications
        </h1>
        <p className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          One daily email at your chosen time with the past 24 hours of flagged issues, capped at 10 with a link to see the rest.
        </p>
      </div>

      <div className={`${cardClass} p-6 space-y-6`}>
        {/* Header + master toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className={`h-5 w-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
            <div>
              <h2 className={`text-base font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Daily issues email</h2>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                Sent once per day in your local timezone
              </p>
            </div>
          </div>
          <button type="button" onClick={() => update('enabled', !settings.enabled)} className={toggleClass(settings.enabled)}>
            <span className={dotClass(settings.enabled)} />
          </button>
        </div>

        {settings.enabled && (
          <>
            {/* Recipients */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                Recipients
              </label>
              {[
                { email: 'recipient_email', name: 'recipient_name' },
                { email: 'recipient_email_2', name: 'recipient_name_2' },
                { email: 'recipient_email_3', name: 'recipient_name_3' },
              ].map((r, i) => {
                // Hide rows beyond filled-in ones, with a + button to reveal next.
                const filled = (settings[r.email] || '').trim().length > 0;
                const prevFilled = i === 0 ? true : (settings[
                  i === 1 ? 'recipient_email' : 'recipient_email_2'
                ] || '').trim().length > 0;
                if (i > 0 && !filled && !prevFilled) return null;
                return (
                  <div key={i} className="flex flex-col sm:flex-row gap-2 mb-3">
                    <div className="sm:w-44">
                      <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        Name (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="Jane Smith"
                        value={settings[r.name] || ''}
                        onChange={e => update(r.name, e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        Email address{i === 0 ? '' : ''}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
                          <input
                            type="email"
                            placeholder={i === 0 ? 'email@example.com' : 'additional@example.com'}
                            value={settings[r.email] || ''}
                            onChange={e => update(r.email, e.target.value)}
                            className={`${inputClass} pl-9`}
                          />
                        </div>
                        {i > 0 && (
                          <button
                            onClick={() => { update(r.email, ''); update(r.name, ''); }}
                            title="Remove"
                            className={`px-2 rounded-md ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#18181b]' : 'text-[#999] hover:text-red-500 hover:bg-[#f0f0f0]'}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className={`text-xs mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#aaa]'}`}>
                Up to 3 recipients. The next slot appears as you fill the previous one.
              </p>
            </div>

            {/* Send time + timezone */}
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Send at</label>
                <input
                  type="time"
                  value={settings.send_time}
                  onChange={e => update('send_time', e.target.value)}
                  className={inputClass}
                  style={{ width: '120px' }}
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Timezone</label>
                <select
                  value={settings.send_timezone}
                  onChange={e => update('send_timezone', e.target.value)}
                  className={inputClass}
                >
                  {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className={`pt-5 border-t flex items-center justify-between ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
              <button
                onClick={handleTest}
                disabled={!recipientCount || testing}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-40 ${
                  isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
                }`}
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Send test now
              </button>
              <div className="flex items-center gap-3">
                {saved && (
                  <span className="flex items-center gap-1 text-sm text-emerald-500">
                    <Check className="w-4 h-4" /> Saved
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                    isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
                  }`}
                >
                  {saving ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving...</span> : 'Save'}
                </button>
              </div>
            </div>
            {testStatus && (
              <div className={`text-xs ${testStatus.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                {testStatus.msg}
              </div>
            )}
          </>
        )}

        {!settings.enabled && (
          <div className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Enable to configure recipients and delivery time.
          </div>
        )}
      </div>

      <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
        Suppressed issues (from your guardrails policy) are also excluded from this email. Manage guardrails in Settings → Issue Guardrails.
      </p>
    </div>
  );
};

export default EmailNotifications;
